import React, {useRef, useState} from 'react'
import {Box, Button, Card, Chip, Typography} from '@mui/material'
import {AutoAwesome as SparkleIcon} from '@mui/icons-material'
import type {SiteBuilderPlanData} from './types'
import {createSingleFlightPlanApproval} from './planApproval'

interface SiteBuilderPlanCardProps {
  data: SiteBuilderPlanData
  onApprove: (message: string) => Promise<void> | void
}

const SiteBuilderPlanCard: React.FC<SiteBuilderPlanCardProps> = ({data, onApprove}) => {
  const [approving, setApproving] = useState(false)
  const [approvalError, setApprovalError] = useState<string | null>(null)

  const approvalRunner = useRef<(() => Promise<void>) | null>(null)
  if (!approvalRunner.current) approvalRunner.current = createSingleFlightPlanApproval(async () => {
    setApproving(true)
    setApprovalError(null)
    try {
      await onApprove('Looks good, let\'s build it')
    } catch (error) {
      setApprovalError(error instanceof Error ? error.message : 'Plan approval failed. Please try again.')
      setApproving(false)
      throw error
    }
  })
  const handleApprove = () => { void approvalRunner.current?.().catch(() => undefined) }

  return (
  <Box sx={{mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5}}>
    {data.sections.map((section, i) => (
      <Card
        key={i}
        variant="outlined"
        sx={{
          px: 2,
          py: 1.5,
          borderRadius: 2,
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 0.5}}>
          <Typography variant="body2" fontWeight={700} sx={{flex: '1 1 12rem', minWidth: {xs: '100%', sm: '12rem'}, overflowWrap: 'anywhere'}}>
            {section.title}
          </Typography>
          <Chip
            label={section.vizType}
            size="small"
            variant="outlined"
            color="primary"
            sx={{
              height: 'auto',
              minHeight: 20,
              maxWidth: '100%',
              fontSize: '0.65rem',
              '& .MuiChip-label': {whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.25},
            }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{display: 'block', lineHeight: 1.5}}>
          {section.dataRationale}
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{display: 'block', mt: 0.5, fontStyle: 'italic'}}>
          {section.toneNote}
        </Typography>
      </Card>
    ))}

    <Button
      variant="contained"
      size="small"
      startIcon={<SparkleIcon sx={{fontSize: 16}} />}
      onClick={handleApprove}
      disabled={approving}
      sx={{
        textTransform: 'none',
        alignSelf: 'flex-start',
        mt: 0.5,
        borderRadius: 2,
        px: 2.5
      }}
    >
      {approving ? 'Starting…' : 'Approve & Start Building'}
    </Button>

    {approvalError && (
      <Typography variant="caption" color="error" role="alert">
        Couldn&apos;t approve the plan. {approvalError}
      </Typography>
    )}

    <Typography variant="caption" color="text.secondary" sx={{mt: -0.5}}>
      Or describe changes in the chat below
    </Typography>
  </Box>
  )
}

export default SiteBuilderPlanCard
